import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  BarChart3,
  Boxes,
  CalendarDays,
  CheckCircle2,
  CircleAlert,
  Clock3,
  Download,
  FileText,
  Loader2,
  PackageSearch,
  Printer,
  Search,
  ShoppingBag,
  Truck,
  Users,
} from "lucide-react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { toast } from "sonner";
import { api, formatError, inr } from "@/lib/api";
import { getAdminUrl } from "@/lib/host";
import { DetailList, MetricCard, StatusBadge } from "./dashboard/DashboardPrimitives";
import {
  CHART_METRICS,
  dateKey,
  groupChartData,
  METRICS,
  periodLabel,
  presetRange,
} from "./dashboard/dashboardUtils";

export const AdminDashboard = ({ onNavigate, onOpenOrders }) => {
  const defaultRange = presetRange("30days");
  const [preset, setPreset] = useState("30days");
  const [range, setRange] = useState({
    start_date: dateKey(defaultRange.start),
    end_date: dateKey(defaultRange.end),
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [chartMetric, setChartMetric] = useState("revenue");
  const [chartMode, setChartMode] = useState("daily");
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/admin/dashboard", { params: range });
      setData(response.data);
    } catch (requestError) {
      setError(formatError(requestError));
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    const query = search.trim();
    if (query.length < 2) {
      setSearchResults([]);
      return undefined;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await api.get("/admin/dashboard/search", { params: { q: query } });
        setSearchResults(response.data.results || []);
      } catch (requestError) {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [search]);

  const chartData = useMemo(
    () => groupChartData(data?.sales_overview || [], chartMode),
    [chartMode, data],
  );
  const chartConfig = CHART_METRICS[chartMetric];

  const setDatePreset = (nextPreset) => {
    setPreset(nextPreset);
    if (nextPreset === "custom") return;
    const nextRange = presetRange(nextPreset);
    setRange({ start_date: dateKey(nextRange.start), end_date: dateKey(nextRange.end) });
  };

  const changeCustomDate = (field, value) => {
    setPreset("custom");
    setRange((current) => ({ ...current, [field]: value }));
  };

  const openOrders = (filter = {}) => {
    onOpenOrders({ ...filter, start_date: range.start_date, end_date: range.end_date });
  };

  const downloadInvoice = async (order) => {
    try {
      const response = await api.get(`/orders/${order.id}/invoice.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `${order.invoice_number || order.id.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (requestError) {
      toast.error(formatError(requestError));
    }
  };

  const printAllPending = () => {
    const ids = data?.invoice_center?.pending_ids || [];
    if (!ids.length) {
      toast.message("There are no invoices pending print.");
      return;
    }
    window.location.assign(getAdminUrl(`/admin/print?ids=${ids.join(",")}`));
  };

  const handleSearchResult = (result) => {
    setSearch("");
    setSearchResults([]);
    if (result.target === "orders") {
      onOpenOrders({ order_id: result.id });
      return;
    }
    onNavigate(result.target);
  };

  const actionTarget = (action) => {
    if (["processing", "shipment", "payments", "delivery"].includes(action.key)) {
      openOrders();
      return;
    }
    if (action.key === "inventory") {
      onNavigate("products");
      return;
    }
    if (action.key === "invoices") printAllPending();
  };

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] p-4 sm:p-6 lg:p-8" data-testid="dashboard-loading">
        <div className="mx-auto max-w-[1600px] space-y-6 animate-pulse">
          <div className="h-20 bg-[#F4F1EA]" />
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {[...Array(8)].map((_, index) => <div key={index} className="h-36 bg-[#F4F1EA]" />)}
          </div>
          <div className="h-[360px] bg-[#F4F1EA]" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] p-8 text-[#1E2A24]" data-testid="dashboard-error">
        <div className="border border-rose-200 bg-rose-50 p-5 text-sm text-rose-800">
          {error}
          <button
            type="button"
            onClick={loadDashboard}
            className="ml-4 border border-rose-300 px-3 py-1 font-semibold hover:bg-rose-100"
            data-testid="dashboard-retry-button"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  const metrics = data?.metrics || {};
  const previous = data?.comparison?.metrics || {};
  const statusEntries = Object.entries(data?.status_counts || {});
  const paymentEntries = Object.entries(data?.payment_overview || {});

  return (
    <div
      className="min-h-screen bg-[#FDFBF7] font-sans text-[#1E2A24]"
      data-testid="dashboard-header"
    >
      <div className="mx-auto max-w-[1600px] space-y-6 p-4 sm:p-6 lg:p-8">
        <header className="border-b border-[#E2DCD2] pb-6" data-testid="welcome-message">
          <div className="flex flex-col justify-between gap-5 xl:flex-row xl:items-end">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#2D5A47]">
                Eira Products · Admin Control Center
              </p>
              <h1 className="mt-2 text-3xl font-bold text-[#1E2A24] sm:text-4xl">
                Operations at a glance
              </h1>
              <p className="mt-2 text-sm text-[#64748B]">
                {data?.range ? periodLabel(data.range) : "Loading period"}
              </p>
            </div>
            <div className="relative w-full xl:max-w-md">
              <label className="sr-only" htmlFor="dashboard-search">
                Search dashboard records
              </label>
              <Search
                className="pointer-events-none absolute left-3 top-3.5 text-[#64748B]"
                size={17}
              />
              <input
                id="dashboard-search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search order, customer, phone, product, SKU or invoice"
                className={[
                  "w-full border border-[#D9C3A5] bg-white px-10 py-3 text-sm",
                  "text-[#1E2A24] outline-none transition-colors placeholder:text-[#94A3B8]",
                  "focus:border-[#2D5A47]",
                ].join(" ")}
                data-testid="global-dashboard-search-input"
              />
              {searchResults.length > 0 && (
                <div
                  className={[
                    "absolute z-30 mt-2 w-full border border-[#D9C3A5]",
                    "bg-white shadow-xl",
                  ].join(" ")}
                >
                  {searchResults.map((result) => (
                    <button
                      key={`${result.type}-${result.id}`}
                      type="button"
                      onClick={() => handleSearchResult(result)}
                      className={[
                        "flex w-full items-center justify-between gap-3 border-b border-[#EDE8DF]",
                        "px-4 py-3 text-left text-sm transition-colors last:border-0",
                        "hover:bg-[#EBF2EE]",
                      ].join(" ")}
                      data-testid={`dashboard-search-result-${result.type}-${result.id}`}
                    >
                      <span>
                        <span className="block font-semibold text-[#1E2A24]">{result.label}</span>
                        <span className="block text-xs text-[#64748B]">{result.detail}</span>
                      </span>
                      <span className="text-[10px] uppercase tracking-wider text-[#2D5A47]">
                        {result.type}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {searching && (
                <Loader2
                  className="absolute right-3 top-3.5 animate-spin text-[#2D5A47]"
                  size={16}
                />
              )}
            </div>
          </div>
        </header>

        <section
          className="border border-[#E2DCD2] bg-white p-4 shadow-sm"
          data-testid="date-filter-selector"
        >
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-wrap gap-2">
              {[
                ["today", "Today"],
                ["yesterday", "Yesterday"],
                ["7days", "Last 7 Days"],
                ["30days", "Last 30 Days"],
                ["this_month", "This Month"],
                ["last_month", "Last Month"],
                ["custom", "Custom Date Range"],
              ].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setDatePreset(key)}
                  className={`border px-3 py-2 text-xs font-semibold transition-colors ${
                    preset === key
                      ? "border-[#2D5A47] bg-[#EBF2EE] text-[#1E4A38]"
                      : "border-[#E2DCD2] text-[#64748B] hover:border-[#B9CABB]"
                  }`}
                  data-testid={`date-filter-${key.replace("days", "days")}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 text-xs text-[#64748B]">
              <CalendarDays size={15} className="text-[#2D5A47]" />
              <span>{data?.range ? periodLabel(data.range) : ""}</span>
            </div>
          </div>
          {preset === "custom" && (
            <div className="mt-4 flex flex-wrap gap-3 border-t border-[#EDE8DF] pt-4">
              <label className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">
                From
                <input
                  type="date"
                  value={range.start_date}
                  max={range.end_date}
                  onChange={(event) => (
                    changeCustomDate("start_date", event.target.value)
                  )}
                  className={[
                    "mt-1 block border border-[#E2DCD2] bg-white px-3 py-2",
                    "text-sm text-[#1E2A24]",
                  ].join(" ")}
                  data-testid="dashboard-custom-start-date"
                />
              </label>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">
                To
                <input
                  type="date"
                  value={range.end_date}
                  min={range.start_date}
                  onChange={(event) => (
                    changeCustomDate("end_date", event.target.value)
                  )}
                  className={[
                    "mt-1 block border border-[#E2DCD2] bg-white px-3 py-2",
                    "text-sm text-[#1E2A24]",
                  ].join(" ")}
                  data-testid="dashboard-custom-end-date"
                />
              </label>
            </div>
          )}
        </section>

        {loading && (
          <div className="text-xs text-[#2D5A47]" data-testid="dashboard-refreshing">
            Refreshing data…
          </div>
        )}

        {!data?.has_data ? (
          <div
            className="border border-[#E2DCD2] bg-white p-12 text-center text-sm text-[#64748B]"
            data-testid="dashboard-no-data"
          >
            No data available for the selected period.
          </div>
        ) : (
          <>
            <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {METRICS.map((definition) => (
                <MetricCard
                  key={definition.key}
                  metric={{ ...definition, value: metrics[definition.key] }}
                  previous={previous[definition.key]}
                  comparisonAvailable={data.comparison?.available}
                  onClick={() => openOrders()}
                />
              ))}
            </section>

            <section
              className="border border-[#E2DCD2] bg-white p-5 shadow-sm sm:p-6"
              data-testid="sales-overview-chart"
            >
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-[#1E2A24]">Sales Overview</h2>
                  <p className="mt-1 text-sm text-[#64748B]">
                    Revenue and order movement for the selected period.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(CHART_METRICS).map(([key, config]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setChartMetric(key)}
                      className={`border px-3 py-2 text-xs font-semibold transition-colors ${
                        chartMetric === key
                          ? "border-[#2D5A47] bg-[#EBF2EE] text-[#1E4A38]"
                          : "border-[#E2DCD2] text-[#64748B] hover:border-[#B9CABB]"
                      }`}
                      data-testid={`chart-metric-switch-${key.replace("net_sales", "net-sales")}`}
                    >
                      {config.label}
                    </button>
                  ))}
                  {["daily", "weekly", "monthly"].map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => setChartMode(mode)}
                      className={`border px-3 py-2 text-xs font-semibold capitalize ${
                        chartMode === mode
                          ? "border-[#8C6D46] bg-[#FBF2E6] text-[#705532]"
                          : "border-[#E2DCD2] text-[#64748B] hover:border-[#B9CABB]"
                      }`}
                      data-testid={`chart-period-${mode}`}
                    >
                      {mode}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mt-6 h-80" data-testid="sales-overview-chart-canvas">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 12, right: 6, left: -12, bottom: 0 }}>
                    <defs>
                      <linearGradient id="dashboardArea" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={chartConfig.color} stopOpacity={0.32} />
                        <stop offset="100%" stopColor={chartConfig.color} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid vertical={false} stroke="#E2DCD2" strokeDasharray="3 3" />
                    <XAxis
                      dataKey="label"
                      tick={{ fill: "#64748B", fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fill: "#64748B", fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(value) => (
                        chartConfig.currency ? `₹${Math.round(value)}` : value
                      )}
                    />
                    <Tooltip
                      contentStyle={{
                        background: "#FFFFFF",
                        border: "1px solid #D9C3A5",
                        borderRadius: 0,
                        color: "#1E2A24",
                      }}
                      formatter={(value) => [
                        chartConfig.currency ? inr(value) : Number(value).toLocaleString("en-IN"),
                        chartConfig.label,
                      ]}
                    />
                    <Area
                      type="monotone"
                      dataKey={chartMetric}
                      stroke={chartConfig.color}
                      strokeWidth={2.5}
                      fill="url(#dashboardArea)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </section>

            <section className="grid gap-6 xl:grid-cols-5">
              <div className="border border-[#E2DCD2] bg-white p-5 shadow-sm xl:col-span-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold">Order Status</h2>
                    <p className="mt-1 text-sm text-[#64748B]">
                      Click a status to open matching orders.
                    </p>
                  </div>
                  <ShoppingBag size={18} className="text-[#2D5A47]" />
                </div>
                <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {statusEntries.map(([status, count]) => (
                    <button
                      key={status}
                      type="button"
                      onClick={() => openOrders({ status })}
                      className={[
                        "border border-[#E2DCD2] bg-[#FDFBF7] p-4 text-left",
                        "transition-colors hover:border-[#B9CABB]",
                      ].join(" ")}
                      data-testid={`order-status-card-${status}`}
                    >
                      <StatusBadge status={status} />
                      <div className="mt-3 font-mono text-2xl font-bold text-[#1E2A24]">
                        {count}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              <div className="border border-[#E2DCD2] bg-white p-5 shadow-sm xl:col-span-2">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold">Action Required</h2>
                    <p className="mt-1 text-sm text-[#64748B]">Only live operational signals.</p>
                  </div>
                  <CircleAlert size={18} className="text-amber-400" />
                </div>
                <DetailList
                  testId="action-required-alerts-list"
                  items={data.action_required || []}
                  emptyText="No actions require attention."
                  renderItem={(action) => (
                    <button
                      key={action.key}
                      type="button"
                      onClick={() => actionTarget(action)}
                      className={[
                        "flex w-full items-center justify-between gap-3 py-4 text-left",
                        "transition-colors hover:text-[#2D5A47]",
                      ].join(" ")}
                      data-testid={`dashboard-action-${action.key}`}
                    >
                      <span className="text-sm text-[#334155]">{action.label}</span>
                      <span className="font-mono text-lg font-bold text-amber-700">
                        {action.count}
                      </span>
                    </button>
                  )}
                />
              </div>
            </section>

            <section className="grid gap-6 xl:grid-cols-2">
              <div
                className="border border-[#E2DCD2] bg-white p-5 shadow-sm"
                data-testid="invoice-center-card"
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h2 className="text-lg font-semibold">Invoice Center</h2>
                    <p className="mt-1 text-sm text-[#64748B]">
                      Generated, print-pending, printed and voided records.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={printAllPending}
                    className={[
                      "inline-flex items-center gap-2 bg-[#2D5A47] px-3 py-2 text-xs",
                      "font-bold text-white transition-colors hover:bg-[#214435]",
                    ].join(" ")}
                    data-testid="invoice-print-all-pending-button"
                  >
                    <Printer size={14} /> Print all pending
                  </button>
                </div>
                <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {[
                    ["Generated", data.invoice_center?.generated],
                    ["Pending", data.invoice_center?.pending],
                    ["Printed", data.invoice_center?.printed],
                    ["Voided", data.invoice_center?.voided],
                  ].map(([label, value]) => (
                    <div key={label} className="border border-[#E2DCD2] bg-[#FDFBF7] p-3">
                      <div
                        className={[
                          "text-[10px] font-semibold uppercase tracking-wider",
                          "text-[#64748B]",
                        ].join(" ")}
                      >
                        {label}
                      </div>
                      <div className="mt-2 font-mono text-2xl font-bold">{value || 0}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div
                className="border border-[#E2DCD2] bg-white p-5 shadow-sm"
                data-testid="payment-analytics-card"
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold">Payment Overview</h2>
                    <p className="mt-1 text-sm text-[#64748B]">
                      Stored payment statuses for this period.
                    </p>
                  </div>
                  <CheckCircle2 size={18} className="text-[#2D5A47]" />
                </div>
                <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {paymentEntries.map(([status, count]) => (
                    <div key={status} className="border border-[#E2DCD2] bg-[#FDFBF7] p-3">
                      <div
                        className={[
                          "text-[10px] font-semibold uppercase tracking-wider",
                          "text-[#64748B]",
                        ].join(" ")}
                      >
                        {status}
                      </div>
                      <div className="mt-2 font-mono text-2xl font-bold text-[#1E2A24]">
                        {count}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="grid gap-6 xl:grid-cols-2">
              <div className="border border-[#E2DCD2] bg-white p-5 shadow-sm">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold">Top Selling Products</h2>
                    <p className="mt-1 text-sm text-[#64748B]">
                      Net product sales after saved discounts.
                    </p>
                  </div>
                  <BarChart3 size={18} className="text-[#2D5A47]" />
                </div>
                <DetailList
                  testId="top-products-list"
                  items={data.top_products || []}
                  emptyText="No product sales in this period."
                  renderItem={(product) => (
                    <button
                      key={product.id || product.name}
                      type="button"
                      onClick={() => onNavigate("products")}
                      className={[
                        "flex w-full items-center justify-between gap-4 py-4 text-left",
                        "transition-colors hover:text-[#2D5A47]",
                      ].join(" ")}
                      data-testid={`dashboard-product-${product.id || product.name}`}
                    >
                      <span>
                        <span className="block text-sm font-semibold text-[#1E2A24]">
                          {product.name}
                        </span>
                        <span className="mt-1 block text-xs text-[#64748B]">
                          {Math.round(product.quantity)} units sold
                        </span>
                      </span>
                      <span className="font-mono text-sm font-bold">{inr(product.revenue)}</span>
                    </button>
                  )}
                />
              </div>
              <div
                className="border border-[#E2DCD2] bg-white p-5 shadow-sm"
                data-testid="low-stock-alerts-card"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-lg font-semibold">Low Stock</h2>
                    <p className="mt-1 text-sm text-[#64748B]">
                      Threshold: {data.inventory?.threshold} units · {" "}
                      {data.inventory?.out_of_stock_count} out of stock
                    </p>
                  </div>
                  <Boxes size={18} className="text-amber-400" />
                </div>
                <DetailList
                  testId="low-stock-alerts-list"
                  items={data.low_stock || []}
                  emptyText="No low-stock products at this threshold."
                  renderItem={(product) => (
                    <button
                      key={product.id}
                      type="button"
                      onClick={() => onNavigate("products")}
                      className={[
                        "flex w-full items-center justify-between gap-4 py-4 text-left",
                        "transition-colors hover:text-amber-200",
                      ].join(" ")}
                      data-testid={`dashboard-low-stock-${product.id}`}
                    >
                    <span className="text-sm font-semibold text-[#1E2A24]">{product.name}</span>
                      <span
                        className={`font-mono text-lg font-bold ${
                          product.stock === 0 ? "text-rose-700" : "text-amber-700"
                        }`}
                      >
                        {product.stock}
                      </span>
                    </button>
                  )}
                />
              </div>
            </section>

            <section className="grid gap-6 xl:grid-cols-4">
              <div
                className="border border-[#E2DCD2] bg-white p-5 shadow-sm"
                data-testid="customer-analytics-card"
              >
                  <Users size={18} className="text-[#5B5BD6]" />
                <h2 className="mt-4 text-lg font-semibold">Customer Analytics</h2>
                <div className="mt-5 space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[#64748B]">Unique buyers</span>
                    <strong>{data.customer_analytics?.unique_buyers}</strong>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#64748B]">Repeat buyers</span>
                    <strong>{data.customer_analytics?.repeat_buyers}</strong>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#64748B]">New customers</span>
                    <strong>{data.customer_analytics?.new_customers}</strong>
                  </div>
                </div>
              </div>
              <div
                className="border border-[#E2DCD2] bg-white p-5 shadow-sm xl:col-span-2"
                data-testid="category-performance-card"
              >
                <h2 className="text-lg font-semibold">Category Performance</h2>
                <DetailList
                  testId="category-performance-list"
                  items={data.category_performance || []}
                  emptyText="No category performance in this period."
                  renderItem={(category) => (
                    <button
                      key={category.id}
                      type="button"
                      onClick={() => onNavigate("products")}
                      className={[
                        "flex w-full items-center justify-between gap-4 py-3 text-left",
                        "transition-colors hover:text-[#2D5A47]",
                      ].join(" ")}
                      data-testid={`dashboard-category-${category.id}`}
                    >
                      <span className="text-sm font-semibold text-[#334155]">{category.name}</span>
                      <span className="font-mono text-sm">{inr(category.revenue)}</span>
                    </button>
                  )}
                />
              </div>
              <div
                className="border border-[#E2DCD2] bg-white p-5 shadow-sm"
                data-testid="delivery-analytics-card"
              >
                <Truck size={18} className="text-sky-300" />
                <h2 className="mt-4 text-lg font-semibold">Shipping Overview</h2>
                <p className="mt-2 text-sm text-[#64748B]">Delayed over 7 days</p>
                <div className="mt-2 font-mono text-3xl font-bold text-amber-700">
                  {data.shipping_overview?.delayed}
                </div>
                <div className="mt-5 flex flex-wrap gap-2">
                  {Object.entries(data.shipping_overview?.statuses || {})
                    .slice(0, 4)
                    .map(([status, count]) => (
                      <span
                        key={status}
                        className="border border-[#E2DCD2] px-2 py-1 text-xs text-[#64748B]"
                      >
                        {status}: {count}
                      </span>
                    ))}
                </div>
              </div>
            </section>

            <section className="grid gap-6 xl:grid-cols-5">
              <div
                className="border border-[#E2DCD2] bg-white p-5 shadow-sm xl:col-span-3"
                data-testid="brand-performance-card"
              >
                <h2 className="text-lg font-semibold">Brand Performance</h2>
                <div className="mt-5 h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={data.brand_performance || []}
                      layout="vertical"
                      margin={{ left: 8, right: 18 }}
                    >
                      <CartesianGrid horizontal={false} stroke="#E2DCD2" />
                      <XAxis type="number" hide />
                      <YAxis
                        dataKey="name"
                        type="category"
                        width={92}
                        tick={{ fill: "#64748B", fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip
                        contentStyle={{
                          background: "#FFFFFF",
                          border: "1px solid #D9C3A5",
                          borderRadius: 0,
                        }}
                        formatter={(value) => [inr(value), "Revenue"]}
                      />
                      <Bar dataKey="revenue" fill="#10B981" radius={0} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div
                className="border border-[#E2DCD2] bg-white p-5 shadow-sm xl:col-span-2"
                data-testid="quick-actions-panel"
              >
                <h2 className="text-lg font-semibold">Quick Actions</h2>
                <div className="mt-4 grid gap-2 sm:grid-cols-2 xl:grid-cols-1">
                  {[
                    ["products", "Manage products", PackageSearch],
                    ["orders", "Review orders", ShoppingBag],
                    ["print", "Invoice settings", FileText],
                    ["settings", "Stock threshold", Clock3],
                  ].map(([target, label, Icon]) => (
                    <button
                      key={target}
                      type="button"
                      onClick={() => (
                        target === "orders" ? openOrders() : onNavigate(target)
                      )}
                      className={[
                        "flex items-center gap-3 border border-[#E2DCD2] bg-[#FDFBF7] px-4 py-3",
                        "text-left text-sm font-semibold text-[#334155] transition-colors",
                        "hover:border-[#2D5A47]/50 hover:text-[#2D5A47]",
                      ].join(" ")}
                      data-testid={`dashboard-quick-action-${target}`}
                    >
                      <Icon size={16} /> {label}
                    </button>
                  ))}
                </div>
              </div>
            </section>

            <section
              className="border border-[#E2DCD2] bg-white shadow-sm"
              data-testid="recent-orders-table"
            >
              <div
                className={[
                  "flex flex-wrap items-center justify-between gap-4 border-b",
                  "border-[#E2DCD2] px-5 py-5",
                ].join(" ")}
              >
                <div>
                  <h2 className="text-lg font-semibold">Recent Orders</h2>
                  <p className="mt-1 text-sm text-[#64748B]">
                    Latest 15 orders for the selected range.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => openOrders()}
                  className={[
                    "border border-[#D9C3A5] px-3 py-2 text-xs font-semibold text-[#334155]",
                    "transition-colors hover:border-[#2D5A47] hover:text-[#2D5A47]",
                  ].join(" ")}
                  data-testid="dashboard-view-all-orders"
                >
                  View all orders
                </button>
              </div>
              <div className="divide-y divide-[#EDE8DF]">
                {(data.recent_orders || []).map((order) => (
                  <div
                    key={order.id}
                    className={[
                      "grid gap-3 px-5 py-4 md:grid-cols-[1.1fr_1.3fr_0.8fr_0.8fr_auto]",
                      "md:items-center",
                    ].join(" ")}
                    data-testid={`recent-orders-row-${order.id}`}
                  >
                    <div>
                      <div className="font-mono text-xs font-bold text-[#1E2A24]">
                        {order.invoice_number || order.id.slice(0, 8)}
                      </div>
                      <div className="mt-1 text-xs text-[#64748B]">
                        {order.created_at
                          ? new Date(order.created_at).toLocaleString("en-IN")
                          : "Date unavailable"}
                      </div>
                    </div>
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-[#1E2A24]">
                        {order.customer}
                      </div>
                      <div className="truncate text-xs text-[#64748B]">{order.user_email}</div>
                    </div>
                    <div className="font-mono text-sm font-bold">{inr(order.total)}</div>
                    <div className="flex flex-wrap gap-2">
                      <StatusBadge status={order.order_status} />
                      <span
                        className={[
                          "border border-[#E2DCD2] px-2 py-1 text-[10px] font-semibold",
                          "uppercase tracking-wide text-[#64748B]",
                        ].join(" ")}
                      >
                        {order.payment_status}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => openOrders({ order_id: order.id })}
                        className={[
                          "p-2 text-[#64748B] transition-colors hover:bg-[#EBF2EE]",
                          "hover:text-[#1E2A24]",
                        ].join(" ")}
                        title="View order"
                        data-testid={`dashboard-view-order-${order.id}`}
                      >
                        <Search size={15} />
                      </button>
                      <button
                        type="button"
                        onClick={() => (
                          window.open(
                            getAdminUrl(`/admin/print/${order.id}?preview=1`),
                            "_blank",
                            "noopener",
                          )
                        )}
                        className={[
                          "p-2 text-[#64748B] transition-colors hover:bg-[#EBF2EE]",
                          "hover:text-[#1E2A24]",
                        ].join(" ")}
                        title="View invoice"
                        data-testid={`dashboard-invoice-order-${order.id}`}
                      >
                        <FileText size={15} />
                      </button>
                      <button
                        type="button"
                        onClick={() => (
                          window.location.assign(getAdminUrl(`/admin/print/${order.id}`))
                        )}
                        className="p-2 text-[#2D5A47] transition-colors hover:bg-[#EBF2EE]"
                        title="Print invoice"
                        data-testid={`dashboard-print-order-${order.id}`}
                      >
                        <Printer size={15} />
                      </button>
                      <button
                        type="button"
                        onClick={() => downloadInvoice(order)}
                        className="p-2 text-sky-700 transition-colors hover:bg-sky-50"
                        title="Download invoice PDF"
                        data-testid={`dashboard-download-order-${order.id}`}
                      >
                        <Download size={15} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
};